import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, toRefs, watch } from '@vue/composition-api';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import CommonCategory from '@/components/common/common-category.component.vue';                     // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import { OfficialDocumentAdminList, TableHeader } from "@/shared/type";
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';                                                    // 定数定義
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import MessageConst from '@/shared/message-const';
import CommonFunction from '@/shared/common-function';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    filterDefs: TableFilterDefs;                                        // フィルタ定義
    filters: TableFilters;                                              // フィルタ条件
    officialDocumentDisplayTableHeaders: TableHeader[];                 // 公文書一覧ヘッダ情報
    groupingAppendTableHeaders: TableHeader[];                          // 発番グループ化時かつ事務連絡・発番指定の両方を表示時に結合するテーブルヘッダ
    officeDocumentListItem: OfficialDocumentAdminList[];                // 公文書一覧情報
    officeContactListItem: OfficialDocumentAdminList[];                 // 公文書一覧情報（事務連絡）
    issueNumberListItem: OfficialDocumentAdminList[];                   // 公文書一覧情報（発番指定）
    totalCount: number;                                                 // 検索件数
    pageOptions: DataOptions;                                           // ページ情報/ソート情報を保存する変数
    organizationId: string;                                             // 選択されたカテゴリの組織ID
    displayTypePullDown: any;                                           // 表示切替の選択肢
    selectedDisplayType: any;                                           // 選択された表示タイプ
    displayType: string;                                                // 表示タイプ（'0'：全て表示する、'1'：発番のみ表示する、'2'：事務連絡のみ表示する）
    cellWidth: any;                                                     // テーブル列ごとのセル幅
    isLoading: boolean;                                                 // テーブルがロード中: true ロード中でない: false
    mailDestinationList: {                                              // メール送信先
        name: string,
        address: string
    }[];
    mailDestinationDialog: boolean;                                     // メール送信先ダイアログ表示切替
    excelDialog: boolean;                                               // excel出力ダイアログ表示切替
    groupSwitch: boolean;                                               // 発番グループ化フラグ（true: ON、false：OFF）
    groupSwitchDisplay: boolean;                                        // 発番グループ化フラグ（一覧表示の切り替え用）
    isDownLoading: boolean;                                             // ファイルがダウンロード中: true ダウンロード中でない: false
    displayConfirmationDialog: boolean;                                 // Excel出力確認ダイアログ表示フラグ
    isFromDetail: boolean;                                              // 詳細、登録、編集、削除、メール通知画面からの遷移：true, それ以外からの遷移：false
    isSelectCategory: boolean                                           // カテゴリ選択フラグ
}

/**
 * 公文書（管理）画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter, CommonCategory },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                             // ルータ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                 // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                    // ページ番号
        const itemsPerPage = ref<number>(linesPerPage[0]);                    // 1ページあたりの行数
        const sortBy = ref<string[]>([]);                                     // ソートキー
        const sortDesc = ref<boolean[]>([]);                                  // ソート順
        let isPageLastData = false;                                           // ページの最終データフラグ
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                       // ウィンドウサイズ
            filterDefs: {                                                     // フィルタ定義
                'freeWord': { title: 'フリーワード', types: 'KeyWordPartialMatch' },
                'issueNumber': { title: '発番', types: 'IssueNumber' },
                'notificationDivision': { title: '一斉／個別', types: 'PullDown' },
                'issueDate': { title: '発出日', types: 'DatePeriod' },
            },
            filters: {},                                                      // フィルタ条件
            officialDocumentDisplayTableHeaders: [],                          // 公文書一覧ヘッダー
            groupingAppendTableHeaders: [],                                   // 発番グループ化時かつ事務連絡・発番指定の両方を表示時に結合するテーブルヘッダ
            officeDocumentListItem: [],                                       // 公文書一覧情報
            officeContactListItem: [],                                        // 公文書一覧情報（事務連絡）
            issueNumberListItem: [],                                          // 公文書一覧情報（発番指定）
            totalCount: 0,                                                    // 検索件数
            pageOptions: {                                                    // ページ情報/ソート情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            organizationId: '',                                               // 選択されたカテゴリの組織ID
            displayTypePullDown: [                                            // 表示切替の選択肢
                {
                    'text': '',
                    'value': ''
                }
            ],
            selectedDisplayType: {                                            // 選択された表示タイプ
                'text': '',
                'value': ''
            },
            displayType: '0',                                                 // 表示タイプ（'0'：全て表示する、'1'：発番のみ表示する、'2'：事務連絡のみ表示する）
            cellWidth: {                                                      // テーブル列ごとのセル幅
                issueNumber: null,
                title: null,
                issueDate: null,
                notificationDivision: null
            },
            isLoading: false,                                                 // テーブルがロード中: true ロード中でない: false
            mailDestinationList: [],                                          // メール送信先
            mailDestinationDialog: false,                                     // メール送信先ダイアログ表示切替
            excelDialog: false,                                               // excel出力ダイアログ表示切替
            groupSwitch: false,                                               // 発番グループ化フラグ（true: ON、false：OFF）
            groupSwitchDisplay: false,                                        // 発番グループ化フラグ（一覧表示の切り替え用）
            isDownLoading: false,                                             // ファイルがダウンロード中: true ダウンロード中でない: false
            displayConfirmationDialog: true,                                  // Excel出力確認ダイアログ表示フラグ
            isFromDetail: false,                                              // 詳細、登録、編集、削除、メール通知画面からの遷移：true, それ以外からの遷移：false
            isSelectCategory: false                                           // カテゴリ選択フラグ
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];        // 公文書一覧のヘッダ色
        const categoryRef = ref<any>(null);                                   // カテゴリー選択コンポーネント
        const tableHeight = SystemConst.TABLE_HEIGHT.OFFICIAL_DOC_LIST;       // テーブル高さ調整用変数
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;              // テーブルのデータ取得中に表示するテキスト
        const filterRef = ref<any>(null);                                     // フィルタコンポーネント
        const commonRef = ref<any>(null);                                     // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        // 公文書一覧ヘッダ情報(初期表示ヘッダ)
        const officialDocumentInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: '発番',
                value: 'issueNumber',
                width: '20%',
                class: tableHeaderClass
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '41%',
                class: tableHeaderClass,
                groupable: false
            },
            {
                text: '発出日',
                value: 'issueDate',
                width: '15%',
                class: tableHeaderClass,
                groupable: false
            },
            {
                text: 'メール通知',
                value: 'notificationDivision',
                width: '20%',
                class: tableHeaderClass,
                groupable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end',
                groupable: false
            }
        ];

        // 公文書一覧ヘッダ情報(発番グループ化用)
        const officialDocumentGroupDisplayTableHeaders: TableHeader[] = [
            {                                                                 // グループ化され表示されない列
                text: '',
                value: 'issueNumber',
                width: '15%',
                class: tableHeaderClass
            },
            {                                                                 // グループ化時の発番ヘッダ表示用
                text: '発番',
                value: 'issueNumberDisplay',
                width: '15%',
                class: tableHeaderClass,
                groupable: false
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '41%',
                class: tableHeaderClass,
                groupable: false
            },
            {
                text: '掲載日',
                value: 'issueDate',
                width: '10%',
                class: tableHeaderClass,
                groupable: false
            },
            {
                text: 'メール通知',
                value: 'notificationDivision',
                width: '15%',
                class: tableHeaderClass,
                groupable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end',
                groupable: false
            }
        ];

        // フィルタバリデーション
        // ・フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.OFFICIAL_DOCUMENT_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];
        // ・発番
        const issueNumberRules = [
            (v: any) =>
                /^[0-9-]*$/.test(v) || CommonFunction.replaceArray(
                    MessageConst.E_REGEX,
                    ['発番', '半角数字、ハイフン（-）']
                ),
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.OFFICIAL_DOCUMENT_LIST_MAX_LENGTH.ISSUE_NUMBER,
                '発番'
            )
        ];

        // ウインドウ上でマウス移動が行われたらcheckCellWidthメソッドを呼ぶ
        window.addEventListener('mousemove', checkCellWidth);

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // 値に変更があった場合
        watch(activeNode, () => {
            if (typeof state.organizationId !== 'undefined') {
                // カテゴリ選択の組織を変更した場合
                // selectCategory()を呼び出す
                selectCategory();
            } else {
                // 初期表示でない場合
                // カテゴリ選択で選択された組織IDを取得し、公文書一覧を取得する
                state.organizationId = activeNode.value[0];
                updateList();
            }
        })

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // カテゴリ取得
            categoryRef.value.getCategory();
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitleを「公文書一覧」に変更
            ctx.root.$store.commit('setMainTitle', '公文書一覧');

            // 初期表示ヘッダ設定
            state.officialDocumentDisplayTableHeaders = officialDocumentInitialDisplayTableHeaders;

            // storeから選択されたカテゴリの組織IDを取得
            state.organizationId = ctx.root.$store.state.officialDocument.activeNode[0];

            // ページ情報/ソート情報を復元する
            pageNation.value = ctx.root.$store.state.officialDocument.pageNation;
            itemsPerPage.value = ctx.root.$store.state.officialDocument.itemsPerPage;
            sortBy.value = ctx.root.$store.state.officialDocument.sortBy;
            sortDesc.value = ctx.root.$store.state.officialDocument.sortDesc;

            // フィルタ条件をすべて復元
            state.filters = ctx.root.$store.state.officialDocument.filters;

            // 詳細、登録、編集、削除、メール通知からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.officialDocument.isFromDetail;

            // 表示タイプを復元
            state.displayType = ctx.root.$store.state.officialDocument.displayType;

            // 発番グループ化フラグを復元
            state.groupSwitchDisplay = ctx.root.$store.state.officialDocument.groupSwitch;
            state.groupSwitch = ctx.root.$store.state.officialDocument.groupSwitch;

            // ページの最終データフラグを復元
            isPageLastData = ctx.root.$store.state.officialDocument.isPageLastData;
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('officialDocument/setIsPageLastData', false);

            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                state.pageOptions.page -= 1;
            }

            // 前回の有効なフィルタのID格納用の配列
            const filterIdList = [];
            // 前回の有効なフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filterのprototypeにkeyがあり、それが有効なフィルタである場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // getInitDisplay()で初期表示データを取得
            await getInitDisplay();

            // 画面表示用の表示タイプを設定
            state.selectedDisplayType = state.displayTypePullDown[Number(state.displayType)];

            // 有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタを設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
                // 有効なフィルタがない場合
            } else {
                if (typeof state.organizationId !== 'undefined') {
                    // 初期表示の場合、公文書一覧を取得
                    // （初期表示以外に処理を行うと組織IDが未設定で権限エラーとなるので、別で処理）
                    updateList();
                }
            }

            // 詳細、登録、編集、削除、メール通知画面からの遷移フラグにfalseを設定
            // trueのままだと、詳細、登録、編集、削除、メール通知画面から遷移後に
            // フィルタを変更したときに1ページ目に戻らなくなるため
            state.isFromDetail = false;
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', false);

        }

        /**
         * カテゴリ選択時のイベント
         */
        async function selectCategory() {
            // システム管理モードの場合
            if (ctx.root.$store.state.isSystemAdminMode) {
                // カテゴリ選択で選択された組織IDを取得し
                state.organizationId = activeNode.value[0];

                // カテゴリ選択フラグにtrueを設定する
                state.isSelectCategory = true;

                // 表示タイプを初期化
                state.displayType = '0';

                // 発番グループ化フラグを初期化
                state.groupSwitch = false;

                // ページ情報を初期化する
                state.pageOptions.page = 1;
                pageNation.value = 1;
                state.pageOptions.itemsPerPage = linesPerPage[0];
                itemsPerPage.value = linesPerPage[0];

                // ソート情報を初期化する
                state.pageOptions.sortBy = [];
                sortBy.value = [];
                state.pageOptions.sortDesc = [];
                sortDesc.value = [];

                // フィルタ条件を初期化する
                state.filters = {};

                // 表示されているフィルタ条件を削除する
                // フィルタ条件変更を契機にupdateFiltersが発火し、
                // 上で初期化したパラメータにより公文書一覧を取得する
                filterRef.value.allClear();

                // 選択したカテゴリに合わせてフィルタを取得し直す
                await getInitDisplay();

                // カテゴリ選択フラグにtrueを設定する
                state.isSelectCategory = false;
            } else {
                // 公文書一覧（一般）画面へ遷移
                router.push('/official-documents/list');
            }
        }

        /**
         * 初期表示用データ取得
         */
        async function getInitDisplay() {
            // 初期表示データ取得APIでデータを取得
            // GET /api/official-documents-admin/parameters
            // パラメータ：
            // ・カテゴリ選択で選択された組織ID
            const response = await ctx.root.$store.dispatch('officialDocument/getAdminListParameters',
                {
                    organizationId: state.organizationId
                }
            );

            // レスポンスから表示切替の選択肢にデータを格納
            state.displayTypePullDown = response.data.displayType;

            // 以下のフィルタ項目に取得情報を設定
            // ・発番
            state.filterDefs['issueNumber'].chkItems = response.data.issueDivision;
            // ・一斉／個別
            state.filterDefs['notificationDivision'].items = response.data.notificationDivision;

            // 以下の項目にフィルタバリデーションを設定
            // ・フリーワード
            state.filterDefs['freeWord'].validator = freeWordRules;
            // ・発番
            state.filterDefs['issueNumber'].validator = issueNumberRules;
        }

        /**
         * 公文書一覧（管理）取得
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // ページ情報/ソート情報、フィルタ条件を保存する
            // 登録・編集・削除・詳細画面から戻った時に復元するため
            ctx.root.$store.commit('officialDocument/setPage', pageNation.value);
            ctx.root.$store.commit('officialDocument/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('officialDocument/setSortBy', sortBy.value);
            ctx.root.$store.commit('officialDocument/setSortDesc', sortDesc.value);
            ctx.root.$store.commit('officialDocument/setFilters', state.filters);
            ctx.root.$store.commit('officialDocument/setDisplayType', state.displayType);
            ctx.root.$store.commit('officialDocument/setGroupSwitch', state.groupSwitch);

            // makeListParametersでAPIパラメータ（組織ID以外）を作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 公文書一覧取得APIでデータを取得する
            // GET /api/official-documents-admin
            // パラメータ：
            // ・組織ID
            // ・ページ番号
            // ・1ページあたりの行数
            // ・ソートキー
            // ・ソート順
            // ・フィルタ条件
            // ・表示タイプ
            // ・発番グループ化フラグ
            const response = await ctx.root.$store.dispatch('officialDocument/getDocumentsAdmin',
                {
                    organizationId: state.organizationId,
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    displayType: state.displayType,
                    isGrouping: Number(state.groupSwitch)
                }
            );

            // API通信のレスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得した公文書一覧のデータを設定する
            state.officeDocumentListItem = response.data.officialDocuments;

            // 一覧の更新前に各配列を初期化
            state.officeContactListItem = [];
            state.issueNumberListItem = [];

            // state.officeDocumentListItem.issueDivision = 0（発番指定）のレコードを
            // state.issueNumberListItemに格納
            // state.officeDocumentListItem.issueDivision = 1（事務連絡）のレコードを
            // state.officeContactListItemに格納
            response.data.officialDocuments.forEach(function (val: OfficialDocumentAdminList) {
                if (val.issueDivision === 0) {
                    state.issueNumberListItem.push(val);
                } else {
                    state.officeContactListItem.push(val);
                }
            });

            // 取得した検索件数を設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.officeDocumentListItem.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // 取得した一覧の内容に応じてテーブルヘッダーを切り替える
            switchTableHeader(state.groupSwitch);

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * 
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // カテゴリ選択時以外の場合
            if (!state.isSelectCategory) {
                // ページ情報/ソート情報をstateへ保存する
                state.pageOptions = options;

                // optionsの中身と比較して、いずれも変更されていなかった場合
                if (pageNation.value === options.page &&
                    itemsPerPage.value === options.itemsPerPage &&
                    sortBy.value === options.sortBy &&
                    sortDesc.value === options.sortDesc
                ) {
                    // 処理を終了する
                    return;
                }

                // ページ番号、1ページあたりの行数を設定する
                pageNation.value = options.page;
                itemsPerPage.value = options.itemsPerPage;

                // ソートキー、ソート順を設定する
                sortBy.value = options.sortBy;
                sortDesc.value = options.sortDesc;

                // 公文書一覧を更新する
                updateList();
            }
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // フィルタ条件をstateへ保存する
            state.filters = filters;

            // 詳細、登録、編集、削除、メール通知画面以外からの遷移時以外
            if (!state.isFromDetail) {
                // ページ番号を初期値(1)へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 公文書一覧を更新する
            updateList();
        }

        /**
         * 表示切替時のイベント
         * 
         * @param displayType 表示タイプ（'0'：全て表示する、'1'：発番のみ表示する、'2'：事務連絡のみ表示する）
         */
        function switchDisplayType(displayType: string) {
            // パラメータの表示タイプをstateへ保存する
            state.displayType = displayType;

            // ページ番号を初期化
            state.pageOptions.page = 1;
            pageNation.value = 1;

            // ソート情報を初期化
            state.pageOptions.sortBy = [];
            sortBy.value = [];
            state.pageOptions.sortDesc = [];
            sortDesc.value = [];

            // フィルタ条件を初期化
            state.filters = {};

            // 表示されているフィルタ条件を削除する
            filterRef.value.allClear();
        }

        /**
         * 公文書登録画面へ遷移する
         */
        function navigationAdd() {
            // 公文書登録画面からの遷移フラグをtrueとする
            // 公文書登録画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);

            // 公文書登録画面へ遷移する
            router.push('/official-documents/add');
        }

        /**
         * メール通知画面へ遷移する
         * 
         * @param officialDocumentId 公文書ID
         */
        async function navigationConfirmMailed(officialDocumentId: string) {
            // メール通知情報取得APIでデータを取得する
            // GET /api/official-documents-admin/confirm-mailed
            // パラメータ：組織ID、公文書ID
            const responseDocument = await ctx.root.$store.dispatch('officialDocument/navigationConfirmMailed',
                {
                    organizationId: state.organizationId,
                    officialDocumentId: officialDocumentId
                }
            );

            if (responseDocument.status !== HttpStatus.OK) {
                // HTTPステータスコードが404の場合
                if (responseDocument.status === HttpStatus.NOT_FOUND) {
                    // 対象データが存在しない旨のメッセージダイアログを表示し、
                    // updateList()を呼び出し、公文書一覧を更新する
                    commonRef.value.displayMessageDialog(responseDocument);
                    updateList();
                    return;
                }

                // HTTPステータスコードが200, 404以外の場合
                // errorDisplayTransitionControlメソッドでエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(responseDocument);
                return;
            }

            // 返却されたメール通知フラグがtrueの場合
            if (responseDocument.data.isMailed) {
                // メール通知済のため処理不可である旨のメッセージダイアログを表示し、処理を終了する
                commonRef.value.simpleDisplayMessageDialog(CommonFunction.replaceArray(MessageConst.E_BECAUSE_OF, ['メール通知済み', 'メール通知画面を表示']));
                return;

            } else {
                // メール通知画面からの遷移フラグをtrueとする
                // メール通知画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
                ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);
                // 返却されたメール通知フラグがfalseの場合
                // （管理向け）メール通知画面へ遷移する
                router.push('/official-documents/mail/' + officialDocumentId);
            }
        }

        /**
         * 公文書編集画面へ遷移する
         * 
         * @param officialDocumentId 公文書ID
         */
        function navigationUpdate(officialDocumentId: string) {
            // 公文書編集画面からの遷移フラグをtrueとする
            // 公文書編集画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);
            // 公文書編集画面へ遷移する
            router.push('/official-documents/edit/' + officialDocumentId);
        }

        /**
         * 公文書削除画面へ遷移する
         * 
         * @param officialDocumentId 公文書ID
         */
        function navigationDelete(officialDocumentId: string) {
            // 公文書削除画面からの遷移フラグをtrueとする
            // 公文書削除画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);
            // isPageLastDataをstoreに保存する
            ctx.root.$store.commit('officialDocument/setIsPageLastData', isPageLastData);
            // 公文書削除画面へ遷移する
            router.push('/official-documents/delete/' + officialDocumentId);
        }

        /**
         * 詳細画面へ遷移する
         * 
         * @param officialDocumentId 公文書ID
         */
        function navigationDetail(officialDocumentId: string) {
            // 詳細画面からの遷移フラグをtrueとする
            // 詳細画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);
            // 公文書詳細画面へ遷移する
            router.push(`/official-documents/detail/${officialDocumentId}`);
        }

        /**
         * 公文書一覧添付ファイルダウンロード
         * 
         * @param officialDocumentId 公文書ID
         * @param fileSequence ファイルシーケンス
         */
        async function download(officialDocumentId: string, fileSequence: number) {
            // 公文書一覧添付ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/official-documents/{officialDocumentId}/{fileSequence}
            // ・officialDocumentId: 公文書ID
            // ・fileSequence:ファイルシーケンス
            const response = await ctx.root.$store.dispatch('officialDocument/download',
                {
                    officialDocumentId: officialDocumentId,
                    fileSequence: fileSequence
                }
            );
            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
        }

        /**
         * Excelファイルのダウンロード
         */
        async function outputExcelFile() {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中にローダーを表示する
            state.isDownLoading = true;

            // ページ情報、ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // Excelファイル名格納（ファイル名：公文書一覧.xlsx）
            const fileName = SystemConst.EXCEL_API_PARAM.OFFICIAL_DOCUMENT_FILE;

            // Excel出力APIでファイルをダウンロードする
            // GET /api/official-documents-admin/excel
            // パラメータ：
            // ・組織ID
            // ・ソートキー
            // ・ソート順
            // ・フィルタ情報
            // ・表示タイプ
            // ・Excelファイル名
            const response = await ctx.root.$store.dispatch('officialDocument/outputExcel',
                {
                    params: {
                        organizationId: state.organizationId,
                        sortKey: params.sortKey,
                        sortOrder: params.sortOrder,
                        filterParams: params.filterParams,
                        displayType: state.displayType
                    },
                    fileName
                });

            // ファイルダウンロード中表示するローダーを非表示にする
            state.isDownLoading = false;

            // Excel出力ダイアログの表示をfalseにする
            state.excelDialog = false;

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            // 現在のウィンドウの幅、高さを保存
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * 各セルの幅を取得する
         */
        function checkCellWidth() {
            // 発番グループ化テーブルの幅調節のため、各カラムの幅を取得しstateへ保存
            // ・issueNumberCell
            state.cellWidth['issueNumber'] = document.getElementsByClassName("issueNumberCell")[0].clientWidth;
            // ・titleCell
            state.cellWidth['title'] = document.getElementsByClassName("titleCell")[0].clientWidth;
            // ・issueDateCell
            state.cellWidth['issueDate'] = document.getElementsByClassName("issueDateCell")[0].clientWidth;
            // ・notificationDivisionCell
            state.cellWidth['notificationDivision'] = document.getElementsByClassName("notificationDivisionCell")[0].clientWidth;
        }

        /* 
         * メール送信先ダイアログ表示
         */
        async function displayMailDestinationsDialog(officialDocumentId: string) {
            // メール送信先取得APIでデータを取得する
            // GET /api/official-documents-admin/mail-destinations
            // パラメータ：
            // ・組織ID
            // ・公文書ID
            const response = await ctx.root.$store.dispatch('officialDocument/getMailDestinations',
                {
                    organizationId: state.organizationId,
                    officialDocumentId: officialDocumentId
                }
            );

            // 通信結果判別
            // HTTPステータスコードが404の場合
            // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 対象データが存在しない旨のメッセージダイアログを表示し、
                    // updateList()を呼び出し、公文書一覧を更新する
                    commonRef.value.displayMessageDialog(response);
                    updateList();
                    return;
                }
                // HTTPステータスコードが200, 404以外の場合
                //errorDisplayTransitionControlメソッドでエラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // HTTPステータスコードが200の場合、
            // メール送信先のダイアログ表示を行う
            state.mailDestinationDialog = true;
            state.mailDestinationList = response.data.mailDestinations;

        }

        /**
         * 発番グループ化変更時のテーブルヘッダー切替
         * 
         * @param groupSwitch true：発番グループ化、false：発番非グループ化
         */
        function switchTableHeader(groupSwitch: boolean) {
            // 取得した一覧データに合わせ、表示ヘッダーを切り替える
            // 発番グループ化されている場合
            if (groupSwitch) {
                // 発番指定のみを表示する場合
                if (state.officeContactListItem.length === 0) {
                    // 表示ヘッダーを発番グループ化用に変更
                    state.officialDocumentDisplayTableHeaders = officialDocumentGroupDisplayTableHeaders;
                // 事務連絡のみを表示する場合
                } else if (state.issueNumberListItem.length === 0) {
                    // 表示ヘッダーを初期表示用に変更
                    state.officialDocumentDisplayTableHeaders = officialDocumentInitialDisplayTableHeaders;
                // 事務連絡、発番指定の両方を表示する場合
                } else {
                    // 「発番」の降順でソートされている場合
                    if ((sortBy.value[0] === 'issueNumber' || sortBy.value[0] === 'issueNumberDisplay') && sortDesc.value[0]) {
                        // 表示ヘッダーを発番グループ化用に変更
                        state.officialDocumentDisplayTableHeaders = officialDocumentGroupDisplayTableHeaders;
                        // 結合するテーブルのヘッダーを初期表示用に変更
                        state.groupingAppendTableHeaders = officialDocumentInitialDisplayTableHeaders;
                    // それ以外の場合
                    } else {
                        // 表示ヘッダーを初期表示用に変更
                        state.officialDocumentDisplayTableHeaders = officialDocumentInitialDisplayTableHeaders;
                        // 結合するテーブルのヘッダーを発番グループ化用に変更
                        state.groupingAppendTableHeaders = officialDocumentGroupDisplayTableHeaders;
                    }
                }
            // 発番グループ化されていない場合
            } else {
                state.officialDocumentDisplayTableHeaders = officialDocumentInitialDisplayTableHeaders;
            }

            // 発番でソートされている場合、表示ヘッダーに合わせて発番のソートキー名を切り替える
            if (sortBy.value[0] === 'issueNumber' || sortBy.value[0] === 'issueNumberDisplay') {
                // 初期表示用のヘッダーの場合
                if (state.officialDocumentDisplayTableHeaders === officialDocumentInitialDisplayTableHeaders) {
                    if (sortBy.value[0] === 'issueNumberDisplay') {
                        // 現在の値が異なる場合
                        // 画面とstoreにソートキー名として"issueNumber"を設定
                        sortBy.value[0] = 'issueNumber';
                        ctx.root.$store.commit('officialDocument/setSortBy', sortBy.value);
                    }
                // 発番グループ化用テーブルの場合
                } else {
                    if (sortBy.value[0] === 'issueNumber') {
                        // 現在の値が異なる場合
                        // 画面とstoreにソートキー名として"issueNumberDisplay"を設定
                        sortBy.value[0] = 'issueNumberDisplay';
                        ctx.root.$store.commit('officialDocument/setSortBy', sortBy.value);
                    }
                }
            }
        }

        /**
         * 発番グループ化スイッチ切り替え時のイベント
         */
        async function switchGrouping() {
            // 公文書一覧を更新する
            // 発番グループ化した際、事務連絡と発番指定がページ跨ぎで交互に表示されないよう、
            // また同じ発出番号のレコードが1ページに表示されるよう取得し直す
            await updateList();

            // 一覧を取得後、テーブルの発番グループ化を切り替える
            state.groupSwitchDisplay = state.groupSwitch;
        }

        return {
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            state: readonly(state),
            categoryRef,
            tableHeight,
            tableLoadingText,
            filterRef,
            commonRef,
            updateOptions,
            updateFilters,
            switchDisplayType,
            navigationAdd,
            navigationConfirmMailed,
            navigationUpdate,
            navigationDelete,
            navigationDetail,
            download,
            outputExcelFile,
            onResize,
            displayMailDestinationsDialog,
            switchGrouping
        };
    }
});
